import React from "react";
import { AbsoluteFill, useCurrentFrame } from "remotion";
import { ThreeCanvas } from "@remotion/three";
import * as THREE from "three";
import { TeenageEngineeringBot } from "./components/AntiSlopHardwareMascots";

// 1. Semi-Realistic Pixar Cat 3D Scene
const PixarCatPreview: React.FC<{ frame: number }> = ({ frame }) => {
  const furMat = new THREE.MeshPhysicalMaterial({
    color: "#D97706",
    roughness: 0.35,
    metalness: 0.05,
    clearcoat: 0.5,
    sheen: 0.9,
    sheenColor: new THREE.Color("#FDE68A"),
  });
  const muzzleMat = new THREE.MeshPhysicalMaterial({ color: "#FEF3C7", roughness: 0.4 });
  const noseMat = new THREE.MeshPhysicalMaterial({ color: "#FB7185", roughness: 0.2 });
  const eyeMat = new THREE.MeshPhysicalMaterial({
    color: "#059669",
    roughness: 0.01,
    metalness: 0.2,
    clearcoat: 1.0,
  });
  const earMat = new THREE.MeshPhysicalMaterial({ color: "#F472B6", roughness: 0.3 });
  const collarMat = new THREE.MeshPhysicalMaterial({ color: "#DC2626", metalness: 0.3, clearcoat: 0.8 });
  const bellMat = new THREE.MeshStandardMaterial({ color: "#FBBF24", metalness: 0.9, roughness: 0.1 });

  const nod = Math.sin(frame * 0.1) * 0.04;
  const earTilt = Math.sin(frame * 0.15) * 0.05;

  return (
    <group position={[0, -0.15 + nod, 0]} rotation={[0.05, 0.3, 0]}>
      {/* Body / Chest */}
      <mesh material={furMat} position={[0, -0.65, -0.1]}>
        <cylinderGeometry args={[0.42, 0.58, 0.95, 24]} />
      </mesh>
      {/* Collar & Bell */}
      <mesh material={collarMat} position={[0, -0.16, 0.05]} rotation={[0.1, 0, 0]}>
        <torusGeometry args={[0.44, 0.05, 12, 24]} />
      </mesh>
      <mesh material={bellMat} position={[0, -0.25, 0.48]}>
        <sphereGeometry args={[0.09, 16, 16]} />
      </mesh>
      {/* Head */}
      <group position={[0, 0.35, 0.1]}>
        <mesh material={furMat} scale={[1, 0.95, 1.05]}>
          <sphereGeometry args={[0.55, 32, 32]} />
        </mesh>
        {/* Anatomical Muzzle (Snout protrusion) */}
        <group position={[0, -0.12, 0.48]}>
          <mesh material={muzzleMat} position={[-0.11, 0, 0]} scale={[0.13, 0.11, 0.12]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          <mesh material={muzzleMat} position={[0.11, 0, 0]} scale={[0.13, 0.11, 0.12]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          {/* Nose */}
          <mesh material={noseMat} position={[0, 0.07, 0.08]} scale={[0.07, 0.05, 0.04]}>
            <sphereGeometry args={[1, 12, 12]} />
          </mesh>
        </group>
        {/* Cat Eyes */}
        <group position={[-0.22, 0.08, 0.46]}>
          <mesh material={eyeMat} scale={[0.12, 0.14, 0.08]}>
            <sphereGeometry args={[1, 20, 20]} />
          </mesh>
          <mesh material={new THREE.MeshBasicMaterial({ color: "#000" })} position={[0, 0, 0.06]} scale={[0.04, 0.12, 0.02]}>
            <sphereGeometry args={[1, 12, 12]} />
          </mesh>
        </group>
        <group position={[0.22, 0.08, 0.46]}>
          <mesh material={eyeMat} scale={[0.12, 0.14, 0.08]}>
            <sphereGeometry args={[1, 20, 20]} />
          </mesh>
          <mesh material={new THREE.MeshBasicMaterial({ color: "#000" })} position={[0, 0, 0.06]} scale={[0.04, 0.12, 0.02]}>
            <sphereGeometry args={[1, 12, 12]} />
          </mesh>
        </group>
        {/* Tall Realistic Ears */}
        <group position={[-0.34, 0.52, -0.05]} rotation={[0.05, 0.2, 0.25 + earTilt]}>
          <mesh material={furMat} scale={[0.22, 0.52, 0.18]}>
            <coneGeometry args={[1, 1.2, 16]} />
          </mesh>
          <mesh material={earMat} position={[0, -0.04, 0.06]} scale={[0.16, 0.42, 0.08]}>
            <coneGeometry args={[1, 1, 16]} />
          </mesh>
        </group>
        <group position={[0.34, 0.52, -0.05]} rotation={[0.05, -0.2, -0.25 - earTilt]}>
          <mesh material={furMat} scale={[0.22, 0.52, 0.18]}>
            <coneGeometry args={[1, 1.2, 16]} />
          </mesh>
          <mesh material={earMat} position={[0, -0.04, 0.06]} scale={[0.16, 0.42, 0.08]}>
            <coneGeometry args={[1, 1, 16]} />
          </mesh>
        </group>
      </group>
    </group>
  );
};

// 2. Modern Dev Humanoid 3D Scene
const DevHumanoidPreview: React.FC<{ frame: number }> = ({ frame }) => {
  const hoodieMat = new THREE.MeshPhysicalMaterial({ color: "#18181B", roughness: 0.6 });
  const skinMat = new THREE.MeshPhysicalMaterial({ color: "#FBBF24", roughness: 0.4 });
  const glassesMat = new THREE.MeshStandardMaterial({ color: "#00E5FF", metalness: 0.8, roughness: 0.2 });
  const headphoneMat = new THREE.MeshStandardMaterial({ color: "#3F3F46", metalness: 0.5, roughness: 0.3 });
  const nod = Math.sin(frame * 0.08) * 0.03;

  return (
    <group position={[0, -0.15 + nod, 0]} rotation={[0, 0.2, 0]}>
      {/* Hoodie Body */}
      <mesh material={hoodieMat} position={[0, -0.7, 0]}>
        <cylinderGeometry args={[0.48, 0.62, 1.1, 24]} />
      </mesh>
      {/* Head */}
      <group position={[0, 0.28, 0]}>
        <mesh material={skinMat} scale={[0.42, 0.52, 0.45]}>
          <sphereGeometry args={[1, 24, 24]} />
        </mesh>
        {/* Glasses */}
        <group position={[0, 0.08, 0.44]}>
          <mesh material={glassesMat} position={[-0.18, 0, 0]}>
            <torusGeometry args={[0.12, 0.015, 8, 20]} />
          </mesh>
          <mesh material={glassesMat} position={[0.18, 0, 0]}>
            <torusGeometry args={[0.12, 0.015, 8, 20]} />
          </mesh>
          <mesh material={glassesMat} position={[0, 0, 0]} scale={[0.12, 0.015, 0.015]}>
            <boxGeometry args={[1, 1, 1]} />
          </mesh>
        </group>
        {/* Studio Over-Ear Headphones */}
        <mesh material={headphoneMat} position={[0, 0.45, 0]}>
          <torusGeometry args={[0.46, 0.04, 12, 32, Math.PI]} />
        </mesh>
        <mesh material={headphoneMat} position={[-0.44, 0.05, 0]}>
          <cylinderGeometry args={[0.18, 0.18, 0.14, 20]} />
        </mesh>
        <mesh material={headphoneMat} position={[0.44, 0.05, 0]}>
          <cylinderGeometry args={[0.18, 0.18, 0.14, 20]} />
        </mesh>
      </group>
    </group>
  );
};

// 3. Retro CRT Monitor-Head Cyborg Scene
const RetroCrtPreview: React.FC<{ frame: number }> = ({ frame }) => {
  const caseMat = new THREE.MeshPhysicalMaterial({ color: "#D4D4D8", roughness: 0.35, metalness: 0.1 });
  const screenMat = new THREE.MeshBasicMaterial({ color: "#0A0A0A" });
  const glowMat = new THREE.MeshStandardMaterial({
    color: "#22C55E",
    emissive: new THREE.Color("#22C55E"),
    emissiveIntensity: 1.8,
  });
  const jacketMat = new THREE.MeshPhysicalMaterial({ color: "#6366F1", roughness: 0.5 });
  const floatY = Math.sin(frame * 0.12) * 0.05;

  return (
    <group position={[0, -0.15 + floatY, 0]} rotation={[0, 0.25, 0]}>
      {/* Streetwear Jacket Body */}
      <mesh material={jacketMat} position={[0, -0.7, 0]}>
        <boxGeometry args={[0.9, 1.1, 0.6]} />
      </mesh>
      {/* CRT Monitor Head */}
      <group position={[0, 0.35, 0]}>
        <mesh material={caseMat} castShadow>
          <boxGeometry args={[1.05, 0.88, 0.85]} />
        </mesh>
        {/* Dark Screen Inset */}
        <mesh material={screenMat} position={[0, 0, 0.44]}>
          <boxGeometry args={[0.82, 0.68, 0.04]} />
        </mesh>
        {/* Pixel Eyes (Green Phosphor Emissive) */}
        <mesh material={glowMat} position={[-0.22, 0.05, 0.47]} scale={[0.14, 0.06, 0.02]}>
          <boxGeometry args={[1, 1, 1]} />
        </mesh>
        <mesh material={glowMat} position={[0.22, 0.05, 0.47]} scale={[0.14, 0.06, 0.02]}>
          <boxGeometry args={[1, 1, 1]} />
        </mesh>
        {/* Pixel Smile */}
        <mesh material={glowMat} position={[0, -0.16, 0.47]} scale={[0.26, 0.04, 0.02]}>
          <boxGeometry args={[1, 1, 1]} />
        </mesh>
        {/* Antenna */}
        <mesh material={caseMat} position={[0.28, 0.65, 0]} rotation={[0, 0, -0.2]}>
          <cylinderGeometry args={[0.02, 0.02, 0.55, 12]} />
        </mesh>
      </group>
    </group>
  );
};

// 4. Geometric Minimalist Abstract Avatar Scene
const GeometricAvatarPreview: React.FC<{ frame: number }> = ({ frame }) => {
  const crystalMat = new THREE.MeshPhysicalMaterial({
    color: "#00E5FF",
    emissive: new THREE.Color("#0088AA"),
    emissiveIntensity: 0.4,
    roughness: 0.05,
    metalness: 0.1,
    transmission: 0.6,
    thickness: 0.8,
    clearcoat: 1.0,
  });
  const ringMat = new THREE.MeshStandardMaterial({
    color: "#E879F9",
    emissive: new THREE.Color("#C026D3"),
    emissiveIntensity: 1.6,
    roughness: 0.1,
  });

  return (
    <group position={[0, 0.05, 0]}>
      {/* Floating Rotating Icosahedron Core */}
      <mesh
        material={crystalMat}
        rotation={[frame * 0.03, frame * 0.04, 0]}
        scale={[0.7, 0.7, 0.7]}
      >
        <icosahedronGeometry args={[1, 0]} />
      </mesh>
      {/* Outer Gyro Orbit Rings */}
      <mesh material={ringMat} rotation={[Math.PI / 3, frame * 0.05, 0]}>
        <torusGeometry args={[1.05, 0.02, 12, 36]} />
      </mesh>
      <mesh material={ringMat} rotation={[-Math.PI / 4, 0, frame * 0.04]}>
        <torusGeometry args={[1.25, 0.02, 12, 36]} />
      </mesh>
    </group>
  );
};

export const CharacterStyleCatalog: React.FC = () => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#07090E",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        color: "#F4F4F5",
        padding: "70px 48px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* HEADER */}
      <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            background: "rgba(24, 28, 40, 0.8)",
            border: "1.5px solid rgba(255, 255, 255, 0.14)",
            padding: "8px 24px",
            borderRadius: 999,
            fontSize: 15,
            fontWeight: 800,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#38BDF8",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#38BDF8" }} />
          @ZURA.AILAB • AI STUDIO WORKSHOP
        </div>
        <h1 style={{ fontSize: 44, fontWeight: 900, margin: 0, letterSpacing: "-0.03em" }}>
          PILIHAN GAYA KARAKTER <span style={{ color: "#FBBF24" }}>SELAIN CHIBI</span>
        </h1>
        <p style={{ fontSize: 20, color: "#94A3B8", margin: 0, fontWeight: 500 }}>
          Pilih arah visual 3D yang paling cocok dengan visi Zura untuk studio podcast & video AI
        </p>
      </div>

      {/* 5 STYLES GRID */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 20,
          marginTop: 20,
          marginBottom: 20,
        }}
      >
        {/* OPTION 1: OP-ZURA HARDWARE BOT */}
        <div
          style={{
            background: "linear-gradient(180deg, #131722 0%, #0C0F17 100%)",
            border: "2px solid #38BDF8",
            borderRadius: 24,
            padding: "20px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.6), 0 0 25px rgba(56, 189, 248, 0.15)",
          }}
        >
          <div
            style={{
              background: "#0284C7",
              color: "#FFF",
              padding: "4px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 900,
              marginBottom: 10,
            }}
          >
            OPSI 1 · RESMI
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 4px 0", color: "#FFF" }}>OP-ZURA</h2>
          <div style={{ fontSize: 13, color: "#38BDF8", fontWeight: 700, marginBottom: 16 }}>
            BENTO HARDWARE BOT
          </div>
          {/* Character Render Stage */}
          <div style={{ width: "100%", height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TeenageEngineeringBot size={220} pose="talking" floating={false} />
          </div>
          <div style={{ fontSize: 14, color: "#CBD5E1", textAlign: "center", lineHeight: 1.45, marginTop: 14 }}>
            Minimalist milled bone resin, layar E-ink soundwave reaktif, dial knob putar oranye. Sangat otentik gadget AI Swedia.
          </div>
        </div>

        {/* OPTION 2: PIXAR STYLIZED CAT */}
        <div
          style={{
            background: "linear-gradient(180deg, #131722 0%, #0C0F17 100%)",
            border: "2px solid #F59E0B",
            borderRadius: 24,
            padding: "20px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.6), 0 0 25px rgba(245, 158, 11, 0.15)",
          }}
        >
          <div
            style={{
              background: "#D97706",
              color: "#FFF",
              padding: "4px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 900,
              marginBottom: 10,
            }}
          >
            OPSI 2
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 4px 0", color: "#FFF" }}>PIXAR CAT</h2>
          <div style={{ fontSize: 13, color: "#F59E0B", fontWeight: 700, marginBottom: 16 }}>
            SEMI-REALISTIC ANIMAL
          </div>
          {/* 3D Canvas */}
          <div style={{ width: "100%", height: 320, borderRadius: 16, overflow: "hidden", background: "#080B12" }}>
            <ThreeCanvas width={180} height={320} camera={{ position: [0, 0, 2.5], fov: 36 }}>
              <ambientLight intensity={0.8} />
              <directionalLight position={[-2, 4, 3]} intensity={3.5} color="#FFF" />
              <directionalLight position={[3, 2, -2]} intensity={4.0} color="#F59E0B" />
              <PixarCatPreview frame={frame} />
            </ThreeCanvas>
          </div>
          <div style={{ fontSize: 14, color: "#CBD5E1", textAlign: "center", lineHeight: 1.45, marginTop: 14 }}>
            Moncong mancung proporsional, rahang dan leher tegas, telinga tegak, duduk tegap di kursi podcast seperti host berwibawa.
          </div>
        </div>

        {/* OPTION 3: DEV HUMANOID */}
        <div
          style={{
            background: "linear-gradient(180deg, #131722 0%, #0C0F17 100%)",
            border: "2px solid #10B981",
            borderRadius: 24,
            padding: "20px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.6), 0 0 25px rgba(16, 185, 129, 0.15)",
          }}
        >
          <div
            style={{
              background: "#059669",
              color: "#FFF",
              padding: "4px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 900,
              marginBottom: 10,
            }}
          >
            OPSI 3
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 4px 0", color: "#FFF" }}>MODERN DEV</h2>
          <div style={{ fontSize: 13, color: "#10B981", fontWeight: 700, marginBottom: 16 }}>
            HUMANOID / PROGRAMMER
          </div>
          {/* 3D Canvas */}
          <div style={{ width: "100%", height: 320, borderRadius: 16, overflow: "hidden", background: "#080B12" }}>
            <ThreeCanvas width={180} height={320} camera={{ position: [0, 0, 2.6], fov: 36 }}>
              <ambientLight intensity={0.7} />
              <directionalLight position={[-2, 4, 3]} intensity={3.5} color="#FFF" />
              <directionalLight position={[3, 2, -2]} intensity={4.5} color="#10B981" />
              <DevHumanoidPreview frame={frame} />
            </ThreeCanvas>
          </div>
          <div style={{ fontSize: 14, color: "#CBD5E1", textAlign: "center", lineHeight: 1.45, marginTop: 14 }}>
            Proporsi manusia dewasa 1:6, hoodie techwear hitam, kacamata cyan neon, dan over-ear studio monitor headphones.
          </div>
        </div>

        {/* OPTION 4: RETRO CRT HEAD */}
        <div
          style={{
            background: "linear-gradient(180deg, #131722 0%, #0C0F17 100%)",
            border: "2px solid #818CF8",
            borderRadius: 24,
            padding: "20px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.6), 0 0 25px rgba(129, 140, 248, 0.15)",
          }}
        >
          <div
            style={{
              background: "#4F46E5",
              color: "#FFF",
              padding: "4px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 900,
              marginBottom: 10,
            }}
          >
            OPSI 4
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 4px 0", color: "#FFF" }}>RETRO CRT</h2>
          <div style={{ fontSize: 13, color: "#818CF8", fontWeight: 700, marginBottom: 16 }}>
            MONITOR-HEAD CYBORG
          </div>
          {/* 3D Canvas */}
          <div style={{ width: "100%", height: 320, borderRadius: 16, overflow: "hidden", background: "#080B12" }}>
            <ThreeCanvas width={180} height={320} camera={{ position: [0, 0, 2.7], fov: 36 }}>
              <ambientLight intensity={0.7} />
              <directionalLight position={[-2, 4, 3]} intensity={3.5} color="#FFF" />
              <directionalLight position={[3, 2, -2]} intensity={4.5} color="#818CF8" />
              <RetroCrtPreview frame={frame} />
            </ThreeCanvas>
          </div>
          <div style={{ fontSize: 14, color: "#CBD5E1", textAlign: "center", lineHeight: 1.45, marginTop: 14 }}>
            Jaket streetwear modern dengan kepala monitor tabung CRT retro. Wajah piksel LED hijau yang ekspresif & berkarakter kuat.
          </div>
        </div>

        {/* OPTION 5: GEOMETRIC ABSTRACT */}
        <div
          style={{
            background: "linear-gradient(180deg, #131722 0%, #0C0F17 100%)",
            border: "2px solid #E879F9",
            borderRadius: 24,
            padding: "20px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.6), 0 0 25px rgba(232, 121, 249, 0.15)",
          }}
        >
          <div
            style={{
              background: "#C026D3",
              color: "#FFF",
              padding: "4px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 900,
              marginBottom: 10,
            }}
          >
            OPSI 5
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 4px 0", color: "#FFF" }}>ABSTRACT AI</h2>
          <div style={{ fontSize: 13, color: "#E879F9", fontWeight: 700, marginBottom: 16 }}>
            GYRO CRYSTAL ORBIT
          </div>
          {/* 3D Canvas */}
          <div style={{ width: "100%", height: 320, borderRadius: 16, overflow: "hidden", background: "#080B12" }}>
            <ThreeCanvas width={180} height={320} camera={{ position: [0, 0, 2.8], fov: 36 }}>
              <ambientLight intensity={0.6} />
              <directionalLight position={[-2, 4, 3]} intensity={3.5} color="#FFF" />
              <directionalLight position={[3, 2, -2]} intensity={5.0} color="#E879F9" />
              <GeometricAvatarPreview frame={frame} />
            </ThreeCanvas>
          </div>
          <div style={{ fontSize: 14, color: "#CBD5E1", textAlign: "center", lineHeight: 1.45, marginTop: 14 }}>
            Kristal icosahedron cyan melayang dengan cincin orbit magenta giroskopik. Pure futuristic, elegan, mewakili kecerdasan digital murni.
          </div>
        </div>
      </div>

      {/* FOOTER CALLOUT */}
      <div
        style={{
          background: "rgba(15, 23, 42, 0.8)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          borderRadius: 16,
          padding: "16px 32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 16, color: "#94A3B8", fontWeight: 600 }}>
          💡 Pilihan mana yang ingin kita pasang sebagai co-host studio Zura berikutnya?
        </span>
        <span style={{ fontSize: 15, color: "#38BDF8", fontWeight: 800 }}>
          TIKTOK PREVIEW • @ZURA.AILAB
        </span>
      </div>
    </AbsoluteFill>
  );
};
